import type { SessionCallAccessLocationState } from '@/constants/sessionCall';

export const PROFESSIONAL_TUTORIAL_DEMO_ACCESS_KEY =
	'plumaa-professional-tutorial-demo-v1' as const;

export const PROFESSIONAL_TUTORIAL_VIDEOCHAMADA_ID =
	'videochamada-prontuario' as const;

/** Fundo da área externa (atrás do card Daily) na demonstração tutorial. */
export const TUTORIAL_DEMONSTRACAO_BACKDROP_BG = '#1F2D3D';

/** Flag no location.state quando a demo abre via animação do Play no tutorial. */
export const PROFESSIONAL_TUTORIAL_DEMO_PLAY_TRANSITION_ENTRY_KEY =
	'plumaa-tutorial-play-entry' as const;

/** Cortina preta na demo: tempo parado antes de revelar o lobby. */
export const TUTORIAL_DEMO_ENTRY_BLACK_HOLD_MS = 400;
/** Cortina preta na demo: duração do fade de transparência. */
export const TUTORIAL_DEMO_ENTRY_BLACK_FADE_MS = 500;
/** Escurecimento rápido ao sair do modal de tutorial antes de abrir a demo. */
export const TUTORIAL_DEMO_ENTRY_QUICK_DARKEN_MS = 120;

export const TUTORIAL_DEMO_ENTRY_BLACK_TOTAL_MS =
	TUTORIAL_DEMO_ENTRY_BLACK_HOLD_MS + TUTORIAL_DEMO_ENTRY_BLACK_FADE_MS;

export const TUTORIAL_DEMO_HEADER_BACK_SELECTOR =
	'[data-tutorial-demo="header-back"]';

export const TUTORIAL_DEMO_HEADER_LOGO_SELECTOR =
	'[data-tutorial-demo="header-logo"]';

export type ProfessionalTutorialHomeReturnState = {
	openProfessionalTutorial: true;
	tutorialId?: typeof PROFESSIONAL_TUTORIAL_VIDEOCHAMADA_ID | string;
};

export type ProfessionalTutorialDemoLocationState =
	SessionCallAccessLocationState & {
		tutorialDemoAccess: typeof PROFESSIONAL_TUTORIAL_DEMO_ACCESS_KEY;
		playTransitionEntry?: typeof PROFESSIONAL_TUTORIAL_DEMO_PLAY_TRANSITION_ENTRY_KEY;
	};

export function createProfessionalTutorialDemoAccessState(options?: {
	fromPlayTransition?: boolean;
}): ProfessionalTutorialDemoLocationState {
	return {
		tutorialDemoAccess: PROFESSIONAL_TUTORIAL_DEMO_ACCESS_KEY,
		fromSessionAccess: true,
		viewerRole: 'professional',
		sessionType: 'P2P',
		sessionStatus: 'STARTED',
		...(options?.fromPlayTransition
			? {
					playTransitionEntry:
						PROFESSIONAL_TUTORIAL_DEMO_PLAY_TRANSITION_ENTRY_KEY,
			  }
			: {}),
	};
}

export function isProfessionalTutorialDemoAccess(
	state: unknown,
): state is ProfessionalTutorialDemoLocationState {
	if (typeof state !== 'object' || state === null) return false;

	const candidate = state as Partial<ProfessionalTutorialDemoLocationState>;
	return (
		candidate.tutorialDemoAccess === PROFESSIONAL_TUTORIAL_DEMO_ACCESS_KEY
	);
}

export function isTutorialDemoPlayTransitionEntry(state: unknown): boolean {
	if (!isProfessionalTutorialDemoAccess(state)) return false;
	return (
		state.playTransitionEntry ===
		PROFESSIONAL_TUTORIAL_DEMO_PLAY_TRANSITION_ENTRY_KEY
	);
}

export function createProfessionalTutorialHomeReturnState(
	tutorialId: string = PROFESSIONAL_TUTORIAL_VIDEOCHAMADA_ID,
): ProfessionalTutorialHomeReturnState {
	return {
		openProfessionalTutorial: true,
		tutorialId,
	};
}

export function parseProfessionalTutorialHomeReturnState(
	state: unknown,
): ProfessionalTutorialHomeReturnState | null {
	if (typeof state !== 'object' || state === null) return null;

	const candidate = state as Partial<ProfessionalTutorialHomeReturnState>;
	if (candidate.openProfessionalTutorial !== true) return null;

	return createProfessionalTutorialHomeReturnState(
		candidate.tutorialId ?? PROFESSIONAL_TUTORIAL_VIDEOCHAMADA_ID,
	);
}
